import React, { useState, useEffect } from 'react';
import { Typography, Box, CircularProgress, Button } from '@mui/material';
import {
  Container,
  StyledTabs,
  StyledTab,
  SidebarContainer,
  ContentContainer,
  LawyerList,
  LawyerItem,
  LawyerSpecialty,
  LawyerLocation,
  SearchBar,
  FilterContainer,
  FilterButton,
  DetailPanel,
  DetailHeader,
  DetailContent,
  DetailActions,
  NoSelectionPlaceholder,
  MapContainer
} from './LawyerCorrespondenceContainer.styles';
import { mockLawyers } from '@utils/mockLawyers';
import { LoadScript, GoogleMap, Marker } from '@react-google-maps/api';

function LawyerCorrespondenceContainer() {
  const [tabValue, setTabValue] = useState(0);
  const [lawyers, setLawyers] = useState(mockLawyers);  // Directly use mockLawyers
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [legalArea, setLegalArea] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleLawyerSelect = (lawyer) => {
    setSelectedLawyer(lawyer);  // Make sure this sets the selected lawyer correctly
  };

  const handleLegalAreaChange = (newArea) => {
    setLegalArea(newArea);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  useEffect(() => {
    setLawyers(mockLawyers);
  }, []);

  const filteredLawyers = lawyers.filter(lawyer => {
    const matchesArea = legalArea === 'all' || lawyer.specialties.includes(legalArea);
    const matchesSearch = lawyer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lawyer.firm.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lawyer.location.city.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesArea && matchesSearch;
  });

  return (
    <Container>
      <SidebarContainer height='85%' marginRight='5px'>
        <Typography variant="h6" sx={{ mb: 2 }}>South Africa</Typography>
        <SearchBar
          placeholder="Search lawyers, firms, or locations..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#C5AB6E',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#C5AB6E',
              },
            },
          }}
        />

        <FilterContainer>
          {['all', 'corporate', 'commercialLaw', 'internationalTrade', 'litigation', ].map((area) => (
            <FilterButton
              key={area}
              active={legalArea === area}
              onClick={() => handleLegalAreaChange(area)}
            >
              {area}
            </FilterButton>
          ))}
        </FilterContainer>

        <LawyerList>
          {filteredLawyers.map(lawyer => (
            <LawyerItem key={lawyer.id} onClick={() => handleLawyerSelect(lawyer)}>
              <Box>
                <Typography variant="subtitle2">{lawyer.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {lawyer.firm}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <LawyerSpecialty>{lawyer.specialties[0]}</LawyerSpecialty>
                <LawyerLocation>{lawyer.location.city}</LawyerLocation>
              </Box>
            </LawyerItem>
          ))}
        </LawyerList>
      </SidebarContainer>

      <ContentContainer>
        <StyledTabs value={tabValue} onChange={handleTabChange}>
          <StyledTab label="Profile" />
          <StyledTab label="Location" />
          <StyledTab label="Contact" />
        </StyledTabs>

        {selectedLawyer ? (
          <Box sx={{ p: 3 }}>
            {tabValue === 0 && (
              <DetailPanel>
                <DetailHeader>
                  <Typography variant="h5">{selectedLawyer.name}</Typography>
                  <Typography variant="subtitle1" color="textSecondary">{selectedLawyer.firm}</Typography>
                </DetailHeader>

                <DetailContent>
                  <Typography variant="body1">{selectedLawyer.bio}</Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6">Specialties:</Typography>
                    <Typography variant="body1">{selectedLawyer.specialties.join(', ')}</Typography>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6">Languages Spoken:</Typography>
                    <Typography variant="body1">{selectedLawyer.languages.join(', ')}</Typography>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6">Admission:</Typography>
                    <Typography variant="body1">{selectedLawyer.admission.court} ({selectedLawyer.admission.year})</Typography>
                  </Box>
                </DetailContent>
              </DetailPanel>
            )}

            {tabValue === 1 && (
              <MapContainer>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6">Office Location</Typography>
                  <Typography variant="body1">{selectedLawyer.location.address}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {selectedLawyer.location.city}, {selectedLawyer.location.province}, South Africa
                  </Typography>
                </Box>
                <Box sx={{ height: 650, width: '100%' }}>
                  <LoadScript googleMapsApiKey="AIzaSyC_vPj3D3Aef1NvtMno66K058F3OjyAzMk">
                    <GoogleMap
                      mapContainerStyle={{ height: '100%', width: '100%' }}
                      center={selectedLawyer.location.coordinates}
                      zoom={15}
                    >
                      <Marker position={selectedLawyer.location.coordinates} />
                    </GoogleMap>
                  </LoadScript>
                </Box>
              </MapContainer>
            )}

            {tabValue === 2 && (
              <DetailPanel>
                <Typography variant="h6">Contact Information</Typography>
                <Box>
                  <Typography variant="subtitle2">Email:</Typography>
                  <Typography variant="body1">{selectedLawyer.contact.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Phone:</Typography>
                  <Typography variant="body1">{selectedLawyer.contact.phone}</Typography>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Assistant:</Typography>
                  <Typography variant="body1">{selectedLawyer.contact.assistant.name}</Typography>
                  <Typography variant="body1">{selectedLawyer.contact.assistant.email}</Typography>
                  <Typography variant="body1">{selectedLawyer.contact.assistant.phone}</Typography>
                </Box>
              </DetailPanel>
            )}
          </Box>
        ) : (
          <NoSelectionPlaceholder>
            <Typography variant="h6" color="textSecondary">Select a lawyer to view details</Typography>
          </NoSelectionPlaceholder>
        )}
      </ContentContainer>
    </Container>
  );
}

export default LawyerCorrespondenceContainer;
